import React from 'react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import styles from '../styles/ReferralForm.module.css';
import { apiService } from '../services/apiService';
import { useInterviewStore } from '../stores';
import { useUiCopy } from '../i18n/useUiCopy';

type Props = {
  applicantId?: string;
  opportunityDiscussedId?: string;
  jobTitle?: string;
  onToast: (text: string, level?: 'success'|'warning'|'error'|'info') => void;
};

const WarmIntroPanel: React.FC<Props> = ({ applicantId, opportunityDiscussedId, jobTitle, onToast }) => {
  const [rName, setRName] = React.useState('');
  const [rEmail, setREmail] = React.useState('');
  const [rPhone, setRPhone] = React.useState<string | undefined>('');
  const [rCompany, setRCompany] = React.useState('');
  const [errors, setErrors] = React.useState<{name?: boolean; email?: boolean}>({});
  const [leadId, setLeadId] = React.useState<string | null>(null);
  const [minted, setMinted] = React.useState<null | { magic_link: string; compose_links: Record<string,string>; intro_blurb: string }>(null);
  const [busy, setBusy] = React.useState(false);
  const [menuEmailOpen, setMenuEmailOpen] = React.useState(false);
  const [menuTextOpen, setMenuTextOpen] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const [showUrl, setShowUrl] = React.useState(false);
  const nameRef = React.useRef<HTMLInputElement>(null);
  const handleInterviewEvent = useInterviewStore(state => state.handleInterviewEvent);
  const copy = useUiCopy();

  React.useEffect(() => { nameRef.current?.focus(); }, []);

  const validateEmail = (email: string) => /[^\s@]+@[^\s@]+\.[^\s@]+/.test(email);

  const createLeadAndMint = async () => {
    const nextErrors = {
      name: rName.trim().length === 0,
      email: !validateEmail(rEmail.trim()),
    };
    setErrors(nextErrors);
    if (nextErrors.name || nextErrors.email) return;

    try {
      setBusy(true);
      const { lead_id } = await apiService.createReferralLead({
        name: rName.trim(),
        email: rEmail.trim(),
        phone: (rPhone ?? '').trim() || undefined,
        company: rCompany.trim() || undefined,
        applicant_id: applicantId,
        job_name: jobTitle,
        rating: 5,
        opportunity_discussed_id: opportunityDiscussedId,
      });
      setLeadId(lead_id);

      const mintedRes = await apiService.mintMagicLink(lead_id);
      setMinted({ magic_link: mintedRes.magic_link, compose_links: mintedRes.compose_links, intro_blurb: mintedRes.intro_blurb });
    } catch (e: any) {
      onToast(e?.message || copy.linksFailed, 'error');
    } finally {
      setBusy(false);
    }
  };

  const markSent = async (channel: string) => {
    if (!leadId) return;
    try {
      await apiService.markIntroSent(leadId, channel);
      onToast(copy.introThanks, 'success');
    } catch (e: any) {
      onToast(e?.message || copy.introStatusFailed, 'warning');
    }
  };

  // Analytics helper
  const openLink = (url: string, label: string) => {
    try { console.log(`Analytics: ${label}`); } catch {}
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCopy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      try { console.log('Analytics: Referral link copied'); } catch {}
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      onToast(copy.copyFailed, 'error');
    }
  };

  return (
    <div className={styles.referralForm}>
      {!minted && (
        <>
          {/* Simple header above the form using brand (orange) style */}
          <div className={styles.headerRow}>
            <div className={`${styles.headerTitle} ${styles.headerTitleSm} ${styles.brandTitle}`}>{copy.referFriend}</div>
          </div>

          <div className={styles.formGroup}>
            <label>{copy.contactName}</label>
            <input ref={nameRef} value={rName} onChange={(e)=>setRName(e.target.value)} placeholder={copy.contactName} className={errors.name ? styles.inputError : ''} />
          </div>
          <div className={styles.formGroup}>
            <label>{copy.contactEmail}</label>
            <input value={rEmail} onChange={(e)=>setREmail(e.target.value)} placeholder={copy.contactEmail} className={errors.email ? styles.inputError : ''} />
          </div>
          <div className={styles.formGroup}>
            <label>{copy.phoneOptional}</label>
            <PhoneInput
              defaultCountry={copy.defaultPhoneCountry}
              placeholder={copy.phoneOptional}
              value={rPhone}
              onChange={(val)=>setRPhone(val || '')}
              withCountryCallingCode
              countryCallingCodeEditable={false}
              international
              addInternationalOption={false}
              className={styles.phoneInput}
            />
          </div>
          <div className={styles.formGroup}>
            <label>{copy.companyOptional}</label>
            <input value={rCompany} onChange={(e)=>setRCompany(e.target.value)} placeholder={copy.companyOptional} />
          </div>
          <div className={styles.formButtons}>
            <button
              onClick={() => handleInterviewEvent('REFERRAL_SUBMITTED')}
              className={styles.secondary}
              disabled={busy}
            >
              {copy.skip}
            </button>
            <button onClick={createLeadAndMint} className={styles.primary} disabled={busy}>
              {busy ? copy.preparingShort : copy.generateLinks}
            </button>
          </div>
        </>
      )}

      {minted && (
        <>
          {/* Header */}
          <div className={styles.headerRow}>
            <div className={`${styles.headerTitle} ${styles.brandTitle}`}>{copy.warmIntroduction}</div>
            <span className={styles.badge}>{copy.recommended}</span>
          </div>
          <div className={styles.subtitle}>{copy.warmIntroSubtitle}</div>

          {/* Stepper */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>{copy.referralSteps}</div>
            <ol className={styles.stepper} aria-label={copy.stepsLabel}>
              <li className={styles.stepItem}>
                <span className={styles.stepNum}>1</span>
                <span className={styles.stepText}>{copy.chooseHow}</span>
              </li>
              <li className={styles.stepItem}>
                <span className={styles.stepNum}>2</span>
                <span className={styles.stepText}>{copy.reviewNote}</span>
              </li>
              <li className={styles.stepItem}>
                <span className={styles.stepNum}>3</span>
                <span className={styles.stepText}>{copy.sendIntro}</span>
              </li>
            </ol>
          </div>

          {/* Channel chooser */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>{copy.chooseChannel}</div>
            <div className={styles.channelRow}>
              {/* Left: Send Email (PRIMARY) */}
              <span className={styles.splitButton}>
                <a
                  className={`${styles.primary} ${styles.comboBtn}`}
                  href={minted.compose_links.gmail}
                  target="_blank" rel="noreferrer"
                  onClick={()=>{ markSent('Email'); }}
                  aria-haspopup="menu" aria-expanded={menuEmailOpen}
                >
                  {/* mail icon */}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <rect x="3" y="5" width="18" height="14" rx="2"></rect>
                    <path d="m3 7 9 6 9-6"></path>
                  </svg>
                  <span>{copy.sendEmail}</span>
                  <span
                    className={styles.comboChevron}
                    onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); setMenuEmailOpen(v=>!v); setMenuTextOpen(false); }}
                    onKeyDown={(e)=>{ if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setMenuEmailOpen(v=>!v); setMenuTextOpen(false); } }}
                    role="button"
                    tabIndex={0}
                    aria-label={copy.moreEmailOptions}
                    aria-expanded={menuEmailOpen}
                    aria-haspopup="menu"
                  >
                    ▾
                  </span>
                </a>
                {menuEmailOpen && (
                  <div className={styles.menu} role="menu" onMouseLeave={()=>setMenuEmailOpen(false)}>
                    <a className={styles.menuItem} href={minted.compose_links.gmail} target="_blank" rel="noreferrer" onClick={()=>{ setMenuEmailOpen(false); markSent('Email'); }}>{copy.gmailWeb}</a>
                    <a className={styles.menuItem} href={minted.compose_links.outlook} target="_blank" rel="noreferrer" onClick={()=>{ setMenuEmailOpen(false); markSent('Email'); }}>{copy.outlookWeb}</a>
                    <a className={styles.menuItem} href={minted.compose_links.mailto} onClick={()=>{ setMenuEmailOpen(false); markSent('Email'); }}>{copy.defaultEmail}</a>
                  </div>
                )}
              </span>

              {/* Right: Send Text (SECONDARY) */}
              <span className={styles.splitButton}>
                <button
                  className={`${styles.secondary} ${styles.comboBtn}`}
                  onClick={()=>{ openLink(minted.compose_links.whatsapp, 'Referral Intro via WhatsApp (default)'); markSent('WhatsApp'); }}
                  aria-haspopup="menu" aria-expanded={menuTextOpen}
                >
                  {/* text icon */}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M21 15a4 4 0 0 1-4 4H7l-4 4V5a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z"/>
                  </svg>
                  <span>{copy.sendText}</span>
                  <span
                    className={styles.comboChevron}
                    onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); setMenuTextOpen(v=>!v); setMenuEmailOpen(false); }}
                    onKeyDown={(e)=>{ if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setMenuTextOpen(v=>!v); setMenuEmailOpen(false); } }}
                    role="button"
                    tabIndex={0}
                    aria-label={copy.moreTextOptions}
                    aria-expanded={menuTextOpen}
                    aria-haspopup="menu"
                  >
                    ▾
                  </span>
                </button>
                {menuTextOpen && (
                  <div className={styles.menu} role="menu" onMouseLeave={()=>setMenuTextOpen(false)}>
                    <a className={styles.menuItem} href={minted.compose_links.sms} onClick={()=>{ setMenuTextOpen(false); openLink(minted.compose_links.sms, 'Referral Intro via SMS'); markSent('SMS'); }}>SMS</a>
                    <a className={styles.menuItem} href={minted.compose_links.whatsapp} target="_blank" rel="noreferrer" onClick={()=>{ setMenuTextOpen(false); openLink(minted.compose_links.whatsapp, 'Referral Intro via WhatsApp'); markSent('WhatsApp'); }}>WhatsApp</a>
                  </div>
                )}
              </span>
            </div>
            <div className={styles.muted}>{copy.channelHint}</div>
          </div>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>{copy.orShareLink}</div>
            <div className={styles.linkCard}>
              <div className={styles.linkRow}>
                <div className={styles.muted}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ marginRight: 6 }}>
                    <path d="M10 13a5 5 0 0 0 7.07 0l1.41-1.41a5 5 0 0 0-7.07-7.07L10 5"/>
                    <path d="M14 11a5 5 0 0 0-7.07 0L5.5 12.43a5 5 0 0 0 7.07 7.07L14 19"/>
                  </svg>
                  {copy.personalReferralLink}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button title={copy.copyReferralLink} className={styles.secondary} onClick={() => handleCopy(minted.magic_link)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <rect x="9" y="9" width="13" height="13" rx="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                    {copied ? copy.copied : copy.copyLink}
                  </button>
                  <a title={copy.openLandingPage} className={styles.primary} href={minted.magic_link} target="_blank" rel="noreferrer" onClick={()=>openLink(minted.magic_link, 'Referral Landing Opened')}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                      <path d="M15 3h6v6"/>
                      <path d="M10 14 21 3"/>
                    </svg>
                    {copy.openReferralPage}
                  </a>
                </div>
              </div>
              {/* Reveal raw URL on demand to reduce visual noise */}
              <div className={styles.linkRevealRow}>
                {!showUrl ? (
                  <button className={styles.revealBtn} onClick={()=>setShowUrl(true)}>{copy.showLink}</button>
                ) : (
                  <div className={styles.linkUrl} title={minted.magic_link}>{minted.magic_link}</div>
                )}
              </div>
            </div>
          </div>

          <div className={styles.notice}>{copy.referralNotice}</div>
        </>
      )}
    </div>
  );
};

export default WarmIntroPanel;
